// Form controls (business app v3 dims, web heights 52 / 36 / 32 / 44):
// Field (label + control + hint/error), TextInput, Chip, Segmented,
// OptionRow, Toggle. Labels 11/700 slate (teal when focused), hints 11/500
// mist, errors in the error colour. Touch layouts raise targets to 48 px (CSS).

import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Check } from 'lucide-react';

export interface FieldProps {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  /** id of the control (generated when omitted and passed to a render-prop child). */
  htmlFor?: string;
  children: ReactNode | ((id: string) => ReactNode);
  className?: string;
}

export function Field({ label, hint, error, htmlFor, children, className }: FieldProps) {
  const gen = useId();
  const id = htmlFor ?? gen;
  return (
    <div className={`e-field ${className ?? ''}`} data-invalid={error ? 'true' : undefined}>
      {label && (
        <label className="e-field-label" htmlFor={id}>
          {label}
        </label>
      )}
      {typeof children === 'function' ? children(id) : children}
      {error ? (
        <span className="e-field-error" role="alert">
          {error}
        </span>
      ) : hint ? (
        <span className="e-field-hint">{hint}</span>
      ) : null}
    </div>
  );
}

export interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Technical value (phone, code, id): LTR + Latin font. */
  ltr?: boolean;
  invalid?: boolean;
  icon?: LucideIcon;
}

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(function TextInput({ ltr, invalid, icon: Icon, className, ...rest }, ref) {
  return (
    <span className={`e-input ${className ?? ''}`} data-invalid={invalid ? 'true' : undefined}>
      {Icon && <Icon aria-hidden="true" />}
      <input ref={ref} dir={ltr ? 'ltr' : undefined} className={ltr ? 'ltr' : undefined} aria-invalid={invalid || undefined} {...rest} />
    </span>
  );
});

/** Field + TextInput in one: <TextField label="الملاحظة" value=… onChange=… /> */
export function TextField({ label, hint, error, ...input }: TextInputProps & { label?: ReactNode; hint?: ReactNode; error?: ReactNode }) {
  return <Field label={label} hint={hint} error={error}>{(id) => <TextInput id={id} invalid={!!error} {...input} />}</Field>;
}

export interface ChipProps {
  selected?: boolean;
  onClick?: () => void;
  icon?: LucideIcon;
  children: ReactNode;
  disabled?: boolean;
  'aria-label'?: string;
}

/** 36 px filter/period chip; selected = ink fill. */
export function Chip({ selected, onClick, icon: Icon, children, disabled, 'aria-label': ariaLabel }: ChipProps) {
  return (
    <button type="button" className="e-chip" aria-pressed={!!selected} onClick={onClick} disabled={disabled} aria-label={ariaLabel}>
      {Icon && <Icon aria-hidden="true" />}
      {children}
    </button>
  );
}

export interface SegmentedOption<V extends string> {
  value: V;
  label: ReactNode;
}

export interface SegmentedProps<V extends string> {
  options: SegmentedOption<V>[];
  value: V;
  onChange: (v: V) => void;
  'aria-label': string;
}

/** 32 px segmented control (radiogroup). */
export function Segmented<V extends string>({ options, value, onChange, 'aria-label': ariaLabel }: SegmentedProps<V>) {
  return (
    <div className="e-seg" role="radiogroup" aria-label={ariaLabel}>
      {options.map((o) => (
        <button key={o.value} type="button" role="radio" aria-checked={o.value === value} className="e-seg-opt" onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

export interface OptionRowProps {
  selected: boolean;
  onSelect: () => void;
  title: ReactNode;
  sub?: ReactNode;
  icon?: LucideIcon;
  disabled?: boolean;
}

/** 44 px selectable row (e.g. payee: «مورد · …» / «مصروف مباشر بلا مورد»). Use inside role="radiogroup". */
export function OptionRow({ selected, onSelect, title, sub, icon: Icon, disabled }: OptionRowProps) {
  return (
    <button type="button" role="radio" aria-checked={selected} className="e-option" onClick={onSelect} disabled={disabled}>
      {Icon && <Icon aria-hidden="true" />}
      <span className="e-option-text">
        <span className="e-option-title">{title}</span>
        {sub && <span className="e-option-sub">{sub}</span>}
      </span>
      {selected && <Check className="e-option-check" aria-hidden="true" />}
    </button>
  );
}

export interface ToggleProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: ReactNode;
  disabled?: boolean;
  description?: ReactNode;
}

/** Switch row (capabilities, settings). */
export function Toggle({ checked, onChange, label, disabled, description }: ToggleProps) {
  const id = useId();
  return (
    <div className="e-toggle-row">
      <span className="e-toggle-text">
        <span id={`${id}-l`} className="e-toggle-label">
          {label}
        </span>
        {description && <span className="e-field-hint">{description}</span>}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-labelledby={`${id}-l`}
        className="e-toggle"
        disabled={disabled}
        onClick={() => onChange(!checked)}
      >
        <span className="e-toggle-knob" aria-hidden="true" />
      </button>
    </div>
  );
}
